import { API_BASE_URL } from '../config';

const baseUrl = API_BASE_URL;

// Helper to add UTM parameters to URL (now expects UTMs to be in formData)
const addUTMParamsToUrl = (url, formData) => {
  try {
    const { 
      utm_source, 
      utm_medium, 
      utm_campaign, 
      utm_content, 
      utm_term,
      utm_id,
      gclid,
      ...rest 
    } = formData || {};
    
    const urlObj = new URL(url, window.location.origin);
    
    // Add UTM parameters to query string
    if (utm_source && utm_source !== '(direct)') urlObj.searchParams.append('utm_source', utm_source);
    if (utm_medium && utm_medium !== '(none)') urlObj.searchParams.append('utm_medium', utm_medium);
    if (utm_campaign) urlObj.searchParams.append('utm_campaign', utm_campaign);
    if (utm_content) urlObj.searchParams.append('utm_content', utm_content);
    if (utm_term) urlObj.searchParams.append('utm_term', utm_term);
    if (utm_id) urlObj.searchParams.append('utm_id', utm_id);
    if (gclid) urlObj.searchParams.append('gclid', gclid);
    
    console.log('[Basket Service] URL with UTMs:', {
      original: url,
      withUtms: urlObj.toString(),
      hasUtms: urlObj.search.length > 0,
      hasGclid: !!gclid
    });
    
    return {
      url: urlObj.toString(),
      data: rest // Return data without UTM params (they're in URL)
    };
  } catch (error) {
    console.error('[Basket Service] Error building URL:', error);
    return { url, data: formData };
  }
};

const isLoggedIn = () => {
  return Boolean(localStorage.getItem("authToken"));
};

// Basket Service Functions
export const storeBasketID = (basketID) => {
  localStorage.setItem("basketID", basketID);
};

export const getBasketID = () => {
  if (localStorage.getItem("basketID") && isLoggedIn()) {
    localStorage.removeItem("basketID");
  }
  return localStorage.getItem("basketID");
};

export const removeBasketID = () => {
  localStorage.removeItem("basketID");
};

// UPDATED: confirmBasket now accepts formData with UTMs
export const confirmBasket = async (token, formData = {}, language = "fr") => {
  // Build URL with UTM parameters
  const { url: finalUrl } = addUTMParamsToUrl(`${baseUrl}users/basket/confirm`, formData);
  
  console.log('[confirmBasket] Calling API with UTMs:', {
    url: finalUrl,
    hasFormData: !!Object.keys(formData).length,
    hasUtmParams: finalUrl.includes('utm_'),
    hasGclid: finalUrl.includes('gclid')
  });

  const response = await fetch(finalUrl, {
    method: "GET", // This is GET in both Angular and React versions
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to confirm the basket");
  }

  return response.json();
};

// UPDATED: confirmWireBasket with UTM support
export const confirmWireBasket = async (token, formData = {}, language = "fr") => {
  // Build base URL with wire transfer parameter
  const baseUrlWithMethod = `${baseUrl}users/basket/confirm?method=external_wire`;
  
  // Add UTM parameters
  const { url: finalUrl } = addUTMParamsToUrl(baseUrlWithMethod, formData);
  
  console.log('[confirmWireBasket] Calling API with UTMs:', {
    url: finalUrl,
    hasFormData: !!Object.keys(formData).length,
    hasUtmParams: finalUrl.includes('utm_'),
    hasGclid: finalUrl.includes('gclid')
  });

  const response = await fetch(finalUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Accept-Language": language,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to confirm the basket (wire)");
  }

  return response.json();
};

export const emptyCart = async (accessToken) => {
  const API_URL = `${baseUrl}users/basket/empty`;

  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to empty cart");
    }

    return await response.json();
  } catch (error) {
    console.error("Error emptying cart:", error);
    throw error;
  }
};

export const applyPromo = async (token, basketId, promoCode, providerId) => {
  const url = new URL(`${baseUrl}users/basket/apply-promo`);
  url.searchParams.append("basketId", basketId);
  url.searchParams.append("promoCode", promoCode);
  url.searchParams.append("providerId", providerId);

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw errorData;
  }

  return response.json();
};

export async function fetchBasket(accessToken) {
  const apiUrl = `${baseUrl}baskets`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching baskets: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetchBaskets:", error.message || error);
    throw error;
  }
}

export const fetchWallet = async (token) => {
  const url = `${baseUrl}wallet/getWallet`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch wallet details");
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching wallet details:", error.message);
    throw error;
  }
};